import styled from 'styled-components';
import React, { ReactNode } from 'react';
import ArticleCardTemplateSimple from './ArticelCardTemplateSimple';
import ArticleCardTemplateRich from './ArticleCardTemplateRich';
import { Article } from './types';

export type ArticleCardWithImageVariantTypes =
  | 'horizontalWide'
  | 'horizontalSquared'
  | 'vertical';

export type ArticleCardWithImageVariant =
  | ArticleCardWithImageVariantTypes
  | ArticleCardWithImageVariantTypes[];

export interface AticleCardWithImageProps extends Article {
  className?: string;
  /** Accept a single variant or an array of variants for
   * each device type (e.g [mobile, tablet, desktop]).
   * Example: ['vertical', 'vertical', 'horizontalWide']
   */
  variant?: ArticleCardWithImageVariant;
  imageNode?: ReactNode;
}

export type ArticleCardContentTemplateProps = Omit<
  AticleCardWithImageProps,
  'imageNode'
>;

function ArticleCardContentTemplate({
  variant,
  className,
  ...articleProps
}: ArticleCardContentTemplateProps) {
  return (
    <ArticleCardContentTemplateCss className={className} variant={variant}>
      <ArticleCardTemplateSimple {...articleProps} />
      <ArticleCardTemplateRich
        {...articleProps}
        largeTitle={
          variant === 'horizontalWide' ||
          [variant?.[2], variant?.[3]].includes('horizontalWide')
        }
      />
    </ArticleCardContentTemplateCss>
  );
}

function AticleCardWithImage({
  imageUrl,
  className,
  variant = 'horizontalWide',
  imageNode,
  ...articleProps
}: AticleCardWithImageProps) {
  return (
    <AticleCardWithImageCss className={className} variant={variant}>
      {imageNode ? (
        <div className="image">{imageNode}</div>
      ) : (
        <ArticleCardImageCss src={imageUrl} />
      )}
      <ArticleCardContentTemplate
        className="content"
        {...articleProps}
        variant={variant}
      />
    </AticleCardWithImageCss>
  );
}

const ArticleCardContentTemplateCss = styled.div<{
  variant?: ArticleCardWithImageVariant;
}>`
  ${p => {
    const { variant } = p;
    const mapVariantToStyle = {
      horizontalWide: `
        & > div:first-child {
          display: none;
        }
      `,
      horizontalSquared: `
        & > div:first-child {
          display: none;
        }
      `,
      vertical: `
        & > div:last-child {
          display: none;
        }
      `,
    };

    if (variant && typeof variant !== 'object')
      return mapVariantToStyle[variant];

    return `
      ${p.theme.breakpoints.maxSm} {
        ${variant && mapVariantToStyle[variant[0]]}
      }
      ${p.theme.breakpoints.sm} {
        ${p.theme.breakpoints.maxLg} {
          ${variant && mapVariantToStyle[variant[1]]}
        }
      }
      ${p.theme.breakpoints.lg} {
        ${p.theme.breakpoints.maxXl} {
          ${variant && mapVariantToStyle[variant[2]]}
        }
      }
      ${p.theme.breakpoints.xl} {
        ${variant && mapVariantToStyle[variant[3] || variant[2]]}
      }
    `;
  }}
`;

const AticleCardWithImageCss = styled.div<{
  variant: ArticleCardWithImageVariant;
}>`
  width: 100%;
  display: flex;
  box-shadow: 0 1px 1px 0 rgba(0, 0, 0, 0.09), 0 0 0 0.5px rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  background: ${p => p.theme.colors.white};

  img {
    transform: scale(1);
    transition: 0.3s ease-in-out;
  }

  &:hover {
    img {
      transform: scale(1.08);
    }
  }

  ${p => {
    const { spacing1 } = p.theme.sizes;
    const variant = p.variant;
    const mapVariantToStyle = {
      horizontalWide: `
        height: 100%;
        
        & > .content {
          width: 33%;
          border-radius: 0 ${spacing1} ${spacing1} 0;
        }

        & > img, & > .image {
          border-radius: ${spacing1} 0 0 ${spacing1};
          width: calc(100% - 33%);
        }
      `,
      horizontalSquared: `
        height: 100%;

        & > .content {
          width: 50%;
          border-radius: 0 ${spacing1} ${spacing1} 0;
        }

        & > img, & > .image {
          width: 50%;
          border-radius: ${spacing1} 0 0 ${spacing1};
        }
      `,
      vertical: `
        height: 100%;
        flex-direction: column;

        & > .content {
          flex: 1;
          border-radius: 0 0 ${spacing1} ${spacing1};
        }

        & > img, & > .image { 
          width: 100%;
          height: auto;
          border-radius: ${spacing1} ${spacing1} 0 0;
        }
      `,
    };

    if (typeof variant !== 'object') return mapVariantToStyle[variant];

    return `
      ${p.theme.breakpoints.maxSm} {
        ${mapVariantToStyle[variant[0]]}
      }
      ${p.theme.breakpoints.sm} {
        ${p.theme.breakpoints.maxLg} {
          ${mapVariantToStyle[variant[1]]}
        }
      }
      
      ${p.theme.breakpoints.lg} {
        ${p.theme.breakpoints.maxXl} {
          ${mapVariantToStyle[variant[2]]}
        }
      }
      ${p.theme.breakpoints.xl} {
        ${mapVariantToStyle[variant[3] || variant[2]]}
      }
    `;
  }}
`;

const ArticleCardImageCss = styled.img`
  object-fit: cover;
`;

export default AticleCardWithImage;
